import { Loadable, Loading } from '@/components/loaders';
import { EmailCampaignGateCard } from '@/features/campaigns/EmailCampaign/components/EmailCampaignGateCard';
import { EmailCampaignPrerequisiteBanner } from '@/features/campaigns/EmailCampaign/components/EmailCampaignPrerequisiteBanner';
import { useEmailCampaignGate } from '@/features/campaigns/EmailCampaign/hooks/useEmailCampaignGate';
import { setEmailCampaignsPagination } from '@/features/campaigns/campaignsSlice';
import RouteConstants from '@/features/router/RouteConstants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useWorkspaceAdmin } from '@/features/workspaces/hooks/useWorkspace';
import { useDelayedBoolean } from '@/hooks/useDelayedBoolean';
import { FrontTable, IconButton, PlusIcon, TablePager } from '@/lib';
import { RootState } from '@/reducers';
import { useGetCampaignsCountV2Query, useLazyGetCampaignsV2Query } from '@/services/endpoints/campaigns';
import { CampaignV2Response, CampaignV2Status } from '@/services/endpoints/campaigns/types';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { CampaignListItem, getEmailCampaignListColumns } from './getEmailCampaignListColumns';
import { mapCampaignStatus } from '@/features/campaigns/v2/Whatsapp/Campaigns/campaignStatus';

const FILTER_CHIPS = [
  {
    id: 'all',
    label: 'campaign:whatsappCampaignFilters.all',
  },
  {
    id: 'active',
    label: 'campaign:whatsappCampaignFilters.active',
  },
  {
    id: 'paused',
    label: 'campaign:whatsappCampaignFilters.paused',
  },
  {
    id: 'completed',
    label: 'campaign:whatsappCampaignFilters.completed',
  },
  {
    id: 'scheduled',
    label: 'campaign:whatsappCampaignFilters.scheduled',
  },
  {
    id: 'drafts',
    label: 'campaign:whatsappCampaignFilters.drafts',
  },
] as const;
type FilterChip = (typeof FILTER_CHIPS)[number]['id'];

const MINIMUM_LOADING_DURATION = 400;

const filterChipToApiStatus = (chip: FilterChip): CampaignV2Status | undefined => {
  if (chip === 'all') {
    return undefined;
  }
  if (chip === 'drafts') {
    return 'DRAFT';
  }

  return chip.toUpperCase() as CampaignV2Status;
};

const formatCampaignSubtitle = (campaign: CampaignV2Response) => {
  const sourceDate = campaign.scheduledStart ?? campaign.createdOn;
  if (!sourceDate) {
    return 'N/A';
  }

  const dateLabel = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(sourceDate));

  return `${dateLabel} · ${campaign.providerType}`;
};

const toCampaignListItem = (campaign: CampaignV2Response): CampaignListItem => {
  const sent = campaign.stats?.totalSent;
  const delivered = campaign.stats?.totalDelivered;
  const read = campaign.stats?.totalRead;
  const deliveredPercent = sent && delivered !== undefined ? Math.round((delivered / sent) * 100) : undefined;
  const readPercent = delivered && read !== undefined ? Math.round((read / delivered) * 100) : undefined;

  return {
    id: campaign.id,
    name: campaign.name,
    subtitle: formatCampaignSubtitle(campaign),
    status: mapCampaignStatus(campaign.status),
    sent,
    delivered,
    deliveredPercent,
    read,
    readPercent,
  };
};

export const EmailCampaignList: FC = () => {
  const { gate, isLoading: isGateLoading } = useEmailCampaignGate();
  const [selectedChip, setSelectedChip] = useState<FilterChip>('all');
  const [searchValue, setSearchValue] = useState('');
  const [campaigns, setCampaigns] = useState<CampaignV2Response[]>([]);

  const { t } = useTranslation();
  const history = useHistory();
  const workspace = useWorkspace();
  const isWorkspaceAdmin = useWorkspaceAdmin();

  const [getCampaignsV2, { data, isFetching: isCampaignsFetching, isLoading: isCampaignsLoading, isUninitialized: isCampaignsUninitialized }] =
    useLazyGetCampaignsV2Query();

  const trimmedSearch = searchValue.trim();
  const validSearchText = trimmedSearch.length > 2 ? trimmedSearch : undefined;
  const apiStatus = filterChipToApiStatus(selectedChip);

  const { data: existingCampaignsCount, isFetching: isExistingCountFetching } = useGetCampaignsCountV2Query({
    workspaceId: workspace.id,
    providerType: 'EMAIL',
  });

  const { data: totalCount, isFetching: isTotalCountFetching } = useGetCampaignsCountV2Query({
    workspaceId: workspace.id,
    searchText: validSearchText,
    status: apiStatus,
    providerType: 'EMAIL',
  });

  const canCreateCampaign = gate === 'ready';
  const hasExistingCampaigns = (existingCampaignsCount ?? 0) > 0;
  const showGateCard = (gate === 'connect-mailtrap' || gate === 'no-audience') && !hasExistingCampaigns;

  const isLoadingDelayed = useDelayedBoolean(isCampaignsLoading, MINIMUM_LOADING_DURATION);
  const shouldShowLoading = isCampaignsLoading || isLoadingDelayed || isCampaignsUninitialized;
  const isFetching = isCampaignsFetching;

  const queryCallback = useCallback(
    (pageNo: number, pageSize: number) =>
      getCampaignsV2({
        workspaceId: workspace.id,
        offset: pageNo * pageSize,
        limit: pageSize,
        searchText: validSearchText,
        status: apiStatus,
        providerType: 'EMAIL',
      }),
    [apiStatus, getCampaignsV2, validSearchText, workspace.id]
  );

  const handleSelectChip = (chipId: FilterChip) => {
    if (chipId !== selectedChip) {
      setSelectedChip(chipId);
    }
  };

  const campaignItems = useMemo(() => campaigns.map(toCampaignListItem), [campaigns]);

  const columns = useMemo(() => getEmailCampaignListColumns(t, { campaigns, isWorkspaceAdmin }), [t, campaigns, isWorkspaceAdmin]);

  if (isGateLoading || (gate === 'connect-mailtrap' && isExistingCountFetching)) {
    return <Loading />;
  }

  if (showGateCard) {
    return <EmailCampaignGateCard variant={gate === 'no-audience' ? 'no-audience' : 'connect-mailtrap'} />;
  }

  return (
    <section className='bg-surface space-y-5 rounded-3xl border border-secondary-border p-5 shadow-sm md:p-6'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
        <div className='space-y-1'>
          <h2 className='text-4xl font-semibold tracking-tight text-slate-900'>
            <Trans
              i18nKey='campaign:emailWorkspace.campaigns.heroTitle'
              components={{
                accent: <span className='text-blue-800' />,
              }}
            />
          </h2>
          <p className='text-secondary-600 max-w-2xl text-sm'>{t('campaign:emailWorkspace.campaigns.heroSubtitle')}</p>
        </div>
        <IconButton
          disabled={!canCreateCampaign}
          leadingIcon={<PlusIcon className='size-5 text-white-100' />}
          className='inline-flex w-full max-w-[211px] justify-center capitalize'
          onClick={() => history.push(RouteConstants.createCampaignEmail)}
          text={t('campaign:emailWorkspace.campaigns.createCampaign')}
        />
      </div>

      {!canCreateCampaign ? <EmailCampaignPrerequisiteBanner variant={gate} /> : null}

      <div className='flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
        <div className='flex flex-wrap gap-2'>
          {FILTER_CHIPS.map((chip) => {
            const isActive = selectedChip === chip.id;

            return (
              <button
                key={chip.id}
                type='button'
                onClick={() => handleSelectChip(chip.id)}
                className={twMerge(
                  'h-8 rounded-full border px-3 text-xs font-semibold uppercase tracking-[0.12em] border-secondary-border bg-white text-secondary-600 hover:bg-gray-100',
                  isActive && 'border-blue-900 bg-blue-50 text-blue-800'
                )}
              >
                {t(`${chip.label}`)}
              </button>
            );
          })}
        </div>

        <div className='flex w-full flex-col gap-2 sm:flex-row lg:w-auto'>
          <input
            type='text'
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder={t('campaign:searchCampaign')}
            className='bg-white text-secondary-500 h-10 w-full rounded-lg border border-secondary-border px-3 text-sm focus:outline-none sm:w-[260px]'
          />
          <button
            type='button'
            className='bg-white h-10 rounded-lg border border-secondary-border px-4 text-sm font-semibold capitalize text-secondary-700 hover:bg-gray-50'
          >
            {t('campaign:filters')}
          </button>
        </div>
      </div>

      <TablePager
        stateSelector={(state: RootState) => state.campaigns.ui.emailCampaigns?.pagination ?? { pageNo: 0, pageSize: 0 }}
        stateUpdater={setEmailCampaignsPagination}
        query={queryCallback}
        queryResponse={data ? data.items : undefined}
        setItems={setCampaigns}
        totalCount={totalCount ?? 0}
        querying={isTotalCountFetching || isFetching}
        wrapperClassNames='mb-0'
      >
        {(pager) => (
          <Loadable isLoading={shouldShowLoading}>
            <FrontTable
              className='overflow-hidden'
              tableData={campaignItems}
              columns={columns}
              footer={pager}
              notFound={t('campaign:tableHeaders.noCampaignsFound')}
              onRowClick={(row) => history.push(RouteConstants.emailCampaignDetails.replace(':campaignId', row.original.id))}
            />
          </Loadable>
        )}
      </TablePager>
    </section>
  );
};
